import React, { Dispatch, SetStateAction, useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { ApiDigitalAdminContact } from '../../services/admin/contact.service';
import { GlobalSwal } from '../../styles/swal.style';
import { Error } from '../../models/error.model';
import { Company, CompanyToken } from '../../models/company.model';
import { Contact } from '../../models/contact.model';
import { modalHide, modalShow } from '../../utils/modal.util';
import { imageCompany } from '../../scripts/image.value.script';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { getError } from '../../languages/translations/response';

import AppLoading from '../Loading';
import { formatLocation } from '../../scripts/format.value.script';

export interface AppModalContactUpdateExtendProps {
  companyForUser: CompanyToken | null,
  meCompanies? : Company[] | null,
  contact: Contact | null,
  contacts?: Contact[] | null,
  contactCompanies: Company[] | null,
  values: {name: string, first_name: string, last_name: string, logo: any, email: string, phone: string, website: string, register_number: string, vat_number: string, street: string, street_number: string, additional: string, administrative_area: string, city: string, zip_code: string, country: string, currency: string, name: string, iban: string, bban: string, bic: string, language: string, qr_type: string},
  setContact: Dispatch<SetStateAction<Contact | null>>,
  setContacts?: Dispatch<SetStateAction<Contact[] | null>>,
  setContactCompanies?: Dispatch<SetStateAction<Company[] | null>>,
  loadAllContacts: Function,
  loadContactsForCompany?: Function,
  restartModalMain: Function
};

let errorResponse: Error, contactResponse: Contact;

const AppModalContactUpdateExtend: React.FunctionComponent<AppModalContactUpdateExtendProps> = ({companyForUser, meCompanies, contact, contacts, contactCompanies, values, setContact, setContacts, setContactCompanies, loadAllContacts, loadContactsForCompany, restartModalMain}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [companies, setCompanies] = useState<string[]>([])

  const submitContactUpdate = async () => {
    setLoadIndicator('on')

    if (companyForUser && contact) {
      let addressStreetNumber = (values.street_number === 'NN' || values.street_number === 'nn') ? values.street_number.toUpperCase() : values.street_number
      let valueIban = values.iban.toUpperCase().trim().replaceAll(' ', '')
      let valueBban = values.bban.toUpperCase().trim().replaceAll(' ', '')
      let valueBic = values.bic.toUpperCase().trim().replaceAll(' ', '')

      await ApiDigitalAdminContact.putContact(companyForUser.id, contact.id, contact.type, values.name, values.first_name, values.last_name, values.logo, values.email, values.phone, values.website, values.register_number, values.vat_number, values.street, addressStreetNumber, values.additional, values.administrative_area, values.city, values.zip_code, values.country, values.currency, values.name, valueIban, valueBban, valueBic, values.language, values.qr_type, companies).then( (response) => {
        if (response.status === 200) {
          contactResponse = response.data
          loadAllContacts(companyForUser.id)
          if (loadContactsForCompany) loadContactsForCompany(companyForUser.id)

          if (contacts && setContacts) {
            setContacts(contacts.map((item) => ((item.id === contact.id) ? contactResponse : item)))
          }
          if (meCompanies && setContactCompanies) {
            setContact(contactResponse)
            setContactCompanies(meCompanies.filter((item) => (contactResponse.data.companies.includes(item.id))))
          }

          GlobalSwal.fire({
            title: lang.labels.successfullyUpdatedContact,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalContactUpdateExtend()
            restartModalMain()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        setLoadIndicator('off')
      })
    }
  }

  const handleChangeCompanies = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    if (companies.includes(event.target.value)) {
      setCompanies(companies.filter((item) => (item !== event.target.value)))
    } else {
      setCompanies([...companies, event.target.value])
    }
  }

  const executeShowModalContactUpdate = () => {
    executeHideModalContactUpdateExtend()
    modalShow('modal-contact-update')
  }

  const executeHideModalContactUpdateExtend = () => {
    modalHide('modal-contact-update-extend')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    setCompanies([])
  }

  return (
    <div id="modal-contact-update-extend" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-body">
            { companyForUser && contactCompanies
              ?
              <>
                <div className="mb-5">
                  <h5 className="mb-3">{lang.labels.wantToExtendChangesToOtherCompanies}</h5>
                  <div className="text-secondary text-justify">
                    {lang.labels.ifYouDoNotWishToExtendContactModificationsToOtherCompanies}
                    <strong className="text-lowercase mx-1">"{lang.labels.update}"</strong>
                    {lang.labels.toContinue}
                  </div>
                </div>
                <div className="mh-350px hover-scroll-y px-3 border-top border-bottom">
                  { contactCompanies.filter(item => item.id !== companyForUser.id).map (( (item, index) => { return (
                    <label key={index} className="d-flex flex-stack cursor-pointer my-3">
                      <span className="d-flex align-items-center me-3">
                        <span className="symbol symbol-50px me-5">
                          <span className="symbol-label bg-light">
                            <img src={imageCompany(item.logo)} alt={lang.labels.logo} className="w-75 h-75" />
                          </span>
                        </span>
                        <span className="d-flex flex-column">
                          <span className="fs-6 fw-bolder">{item.name}</span>
                          <small className="text-muted">{formatLocation(lang, item.address)}</small>
                        </span>
                      </span>
                      <span className="form-check form-check-custom form-check-solid">
                        <input className="form-check-input" type="checkbox" value={item.id} checked={companies.includes(item.id)} onChange={handleChangeCompanies} />
                      </span>
                    </label>
                  )}))}
                </div>
              </>
              :
              <AppLoading></AppLoading>
            }
          </div>
          <div className="modal-footer flex-stack">
            <button className="btn btn-secondary mx-2" type="button" onClick={executeShowModalContactUpdate}>
              <div className="d-flex align-items-center">
                <span className="svg-icon svg-icon-3 ms-0">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect opacity="0.5" x="6" y="11" width="13" height="2" rx="1" fill="black" />
                    <path d="M8.56569 11.4343L12.75 7.25C13.1642 6.83579 13.1642 6.16421 12.75 5.75C12.3358 5.33579 11.6642 5.33579 11.25 5.75L5.70711 11.2929C5.31658 11.6834 5.31658 12.3166 5.70711 12.7071L11.25 18.25C11.6642 18.6642 12.3358 18.6642 12.75 18.25C13.1642 17.8358 13.1642 17.1642 12.75 16.75L8.56569 12.5657C8.25327 12.2533 8.25327 11.7467 8.56569 11.4343Z" fill="black" />
                  </svg>
                </span>
                {lang.labels.back}
              </div>
            </button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitContactUpdate}>
              <span className="indicator-label">{lang.labels.update}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppModalContactUpdateExtend;
